// Shared target-type detection used by the dashboard UI (live preview)
// and the backend scan pipeline. Keep this file dependency-free so it is
// safe to import from both the browser and server bundles.

export type DetectedTargetType =
  | "email"
  | "domain"
  | "ip"
  | "wallet"
  | "phone"
  | "username"
  | "organization"
  | "person";

const ORG_SUFFIX_RE =
  /\b(inc|llc|ltd|limited|corp|corporation|company|co|gmbh|sarl|sa|plc|holdings?|group|labs?|studios?|foundation|university|college|bank|partners|capital|ventures|technologies|systems|solutions|services|industries)\b\.?$/i;

export function detectTargetType(input: string): DetectedTargetType {
  const v = (input ?? "").trim();
  if (!v) return "organization";

  // Email
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return "email";

  // EVM wallet
  if (/^0x[a-fA-F0-9]{40}$/.test(v)) return "wallet";

  // IPv4
  if (/^(?:(?:25[0-5]|2[0-4]\d|1?\d?\d)\.){3}(?:25[0-5]|2[0-4]\d|1?\d?\d)$/.test(v)) return "ip";

  // Phone: must start with + (intl) OR be a digit-heavy token (8+ digits total)
  const digitCount = (v.match(/\d/g) ?? []).length;
  if (/^\+[0-9][0-9 .()\-]{6,}$/.test(v) && digitCount >= 7) return "phone";
  if (/^[0-9][0-9 .()\-]{8,}$/.test(v) && digitCount >= 9 && !/[a-z]/i.test(v)) return "phone";

  // Domain / URL
  if (/^(?:https?:\/\/)?(?:[a-z0-9-]+\.)+[a-z]{2,}(?:[\/?#].*)?$/i.test(v)) return "domain";

  // Explicit handle (@something)
  if (/^@[a-z0-9_.\-]{2,32}$/i.test(v)) return "username";

  // Multi-token letter string → person, unless it ends with a company suffix
  if (/\s/.test(v) && /^[\p{L}][\p{L}\s.'\-]+$/u.test(v)) {
    if (ORG_SUFFIX_RE.test(v)) return "organization";
    return "person";
  }

  // Single token containing handle-ish punctuation/digits → username
  if (/^[a-z0-9_.\-]{3,32}$/i.test(v) && /[0-9_.\-]/.test(v)) return "username";

  // Otherwise treat as organization (single word brand, etc.)
  return "organization";
}
